import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  findGlows,
  findPastGlows,
  findUpcomingGlows,
  findGlow,
  mutateGlow,
  mutateGlowStatus,
} from '@/graphs/gql';
import globalShared from '@/shared';

dayjs.extend(relativeTime);

export default function glowShared(): { [key: string]: any } {
  const {
    ref,
    makeGraphRequest,
    getAuthToken,
    dateAndTime,
  } = globalShared();

  const loading = ref(false);
  const dialog = ref(false);

  async function getGlows(
    take = 20,
    skip = 0,
    searchText = '',
    sortBy = 'CreatedAt',
    sortAsc = false,
    status: string | null = null,
    organizationId = '',
  ): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findGlows,
      {
        take, skip, searchText, sortBy, sortAsc, status, orgId: organizationId,
      },
      getAuthToken(),
    );

    return response.data.getGlows;
  }

  async function getPastGlows(
    take = 20,
    skip = 0,
    searchText = '',
    sortBy = 'CreatedAt',
    sortAsc = false,
    status: string | null = null,
    organizationId = '',
  ): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findPastGlows,
      {
        take, skip, searchText, sortBy, sortAsc, status, orgId: organizationId,
      },
      getAuthToken(),
    );

    return response.data.getPastGlows;
  }

  async function getUpcomingGlows(
    take = 20,
    skip = 0,
    searchText = '',
    sortBy = 'CreatedAt',
    sortAsc = false,
    status: string | null = null,
    organizationId = '',
  ): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findUpcomingGlows,
      {
        take, skip, searchText, sortBy, sortAsc, status, orgId: organizationId,
      },
      getAuthToken(),
    );

    return response.data.getUpcomingGlows;
  }

  async function getGlow(id: string, orgId?: string): Promise<any> {
    const response = await makeGraphRequest(
      'query',
      findGlow(id, orgId),
      {},
      getAuthToken(),
    );
    return response.data.getGlow;
  }

  async function createGlow(orgId: string, input: any) {
    const response = await makeGraphRequest(
      'mutation',
      mutateGlow,
      { orgId, input },
      getAuthToken(),
    );

    // Check for GraphQL errors and throw them so they can be caught
    if (response?.errors && response.errors.length > 0) {
      const errorMessage = response.errors[0]?.message || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    return response.data.mutateGlow;
  }

  async function updateGlow(orgId: string, input: any) {
    const response = await makeGraphRequest(
      'mutation',
      mutateGlow,
      { orgId, input },
      getAuthToken(),
    );

    // Check for GraphQL errors and throw them so they can be caught
    if (response?.errors && response.errors.length > 0) {
      const errorMessage = response.errors[0]?.message || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    return response.data.mutateGlow;
  }

  async function updateGlowStatus(glowId: string, status: string, orgId: string) {
    const response = await makeGraphRequest(
      'mutation',
      mutateGlowStatus,
      { glowId, status, orgId },
      getAuthToken(),
    );
    return response.data.mutateGlowStatus;
  }

  function formatRelativeDateTime(dateString: string): string {
    if (!dateString) return '';

    const date = dayjs(dateString);
    const now = dayjs();
    const today = now.startOf('day');
    const yesterday = today.subtract(1, 'day');
    const tomorrow = today.add(1, 'day');
    const dateStart = date.startOf('day');

    let dayLabel = '';

    if (dateStart.isSame(today)) {
      dayLabel = 'Today';
    } else if (dateStart.isSame(yesterday)) {
      dayLabel = 'Yesterday';
    } else if (dateStart.isSame(tomorrow)) {
      dayLabel = 'Tomorrow';
    } else {
      dayLabel = date.format('MM/DD/YYYY');
    }

    const time = date.format('h:mm a');
    return `${dayLabel} at ${time}`;
  }

  return {
    loading,
    dialog,
    getGlows,
    getPastGlows,
    getUpcomingGlows,
    getGlow,
    createGlow,
    updateGlow,
    updateGlowStatus,
    dateAndTime,
    formatRelativeDateTime,
  };
}
